import { priceStrFormatter } from "../../../../utils/formatters/priceStrFormatter";

// Display helpers shared by the DoorMargin components. Every null is "--"
// (plan §4 named null cases: blended pct with no techs, margin at zero price,
// per-door figures at zero doors, scale at zero predicted).

export const money = (cents) => (cents == null || cents === "" ? "--" : priceStrFormatter(String(Math.round(Number(cents)))));

export const pct = (frac) => {
  if (frac == null || frac === "") return "--";
  const n = Number(frac);
  if (!Number.isFinite(n)) return "--";
  return `${Math.round(n * 100)}%`;
};

export const num = (v, dp = 1) => {
  if (v == null || v === "") return "--";
  const n = Number(v);
  if (!Number.isFinite(n)) return "--";
  return Number.isInteger(n) ? String(n) : n.toFixed(dp);
};

export const fmtDay = (ms) => {
  const n = parseInt(ms, 10);
  if (!Number.isFinite(n)) return "";
  return new Date(n).toLocaleDateString("en-US", {
    timeZone: "America/Chicago",
    month: "numeric",
    day: "numeric",
    year: "2-digit",
  });
};

export const toCents = (display) => {
  const digits = String(display || "").replace(/[^0-9]/g, "");
  return digits ? String(parseInt(digits, 10)) : "0";
};

/**
 * Standard refinish discount (2026-09-27, owner: almost every refinish carries
 * ~10% off as normal operation). A VIEW over one Recipe-mode refinish figure the
 * snapshot already stores — nothing is recomputed server-side:
 *   price = round(catalog × (1 − d)), pay = round(price × payPct) (refinishers
 *   are paid on the discounted price, as payroll does), materials unchanged,
 *   net = price − pay − materials − overhead.
 * Maintenance never gets it (its invoice lines are not discountable), and
 * Actual mode never needs it (realized prices already carry real discounts).
 * Returns the figure unchanged when there's no door price or no discount.
 */
export const DEFAULT_DISCOUNT_PCT = 10;

export const discountPctValue = (raw) => {
  const n = parseFloat(raw);
  return Number.isFinite(n) ? Math.min(50, Math.max(0, n)) : 0; // same 0–50 range as the mobile stepper
};

export const applyStandardDiscount = (fig, { discountPct, payPct, overheadCents }) => {
  if (!fig || fig.doorPriceCents == null || !(discountPct > 0)) return fig;
  const list = Number(fig.doorPriceCents);
  const price = Math.round(list * (1 - discountPct / 100));
  // null / "" pct (blended with no active techs) must stay "no pay figure" —
  // Number(null) is 0, which would show $0 pay and a fake net.
  const p = payPct == null || payPct === "" ? NaN : Number(payPct);
  const payCents = Number.isFinite(p) ? Math.round(price * p) : null;
  if (payCents == null) return { ...fig, listPriceCents: list, doorPriceCents: price, payCents: null, netCents: null, marginBp: null };
  const netCents = price - payCents - (Number(fig.materialsCents) || 0) - (Number(overheadCents) || 0);
  return {
    ...fig,
    listPriceCents: list,
    doorPriceCents: price,
    payCents,
    netCents,
    marginBp: price > 0 ? Math.round((netCents / price) * 10000) : null,
  };
};

/** The refinish overhead a snapshot charged (per-kind total, else the pre-2026-09-27 sum). */
export const refinishOverheadOf = (ovh) =>
  !ovh
    ? null
    : ovh.refinishCents ?? (ovh.softwareCents || 0) + (ovh.marketingBlendedCents || 0) + (ovh.damageCents || 0) + (ovh.otherCents || 0);

/**
 * Refinishers in report order (2026-09-27 — no blended view; the report opens on
 * the first): active first, then seniority ascending (1 = most senior; unset
 * last, like payroll's '999'), then name. `seniority` = { [userId]: number|null }.
 */
export const orderRefinishers = (perRefinisher, seniority) => {
  const sen = (id) => {
    const n = seniority ? seniority[id] : null;
    return Number.isFinite(n) ? n : Infinity;
  };
  return [...(perRefinisher || [])].sort(
    (a, b) =>
      (a.active === false) - (b.active === false) ||
      sen(a.userId) - sen(b.userId) ||
      String(a.name || "").localeCompare(String(b.name || ""))
  );
};
